import React, { useEffect, useRef, useState } from 'react';
import {
  Camera,
  CheckCircle2,
  Loader2,
  Mic,
  RotateCcw,
  ShieldCheck,
  Square,
  Upload,
  Video,
} from 'lucide-react';

import { AttachmentPhotoGallery } from '@/shared/components/AttachmentPhotoGallery';
import { Input } from '@/shared/ui/input';
import { ErrorState, getErrorMessage } from '@/shared/errors';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Progress } from '@/shared/ui/progress';

import { usePunchWalkthrough, usePunchWalkthroughMutations } from '../hooks';
import { WalkthroughItemFields, walkthroughDraftForReview } from './WalkthroughItemFields';
import type {
  CostCode,
  LocationMinimal,
  PunchListItemType,
  PunchWalkthroughDraft,
  PunchWalkthroughDraftUpdate,
  PunchWalkthroughUploadResponse,
  Trade,
  TradePartnerMinimal,
  UserMinimal,
} from '../types';


const MAX_RECORDING_SECONDS = 5 * 60;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const MAX_RECORDING_BYTES = 96 * 1024 * 1024;
const pendingReview = (draft: PunchWalkthroughDraft) => draft.review_status
  ? draft.review_status === 'PENDING' : !draft.published_item;

interface PunchWalkthroughDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: number;
  trades: Trade[];
  itemTypes: PunchListItemType[];
  locations: LocationMinimal[];
  users?: UserMinimal[];
  tradePartners?: TradePartnerMinimal[];
  costCodes?: CostCode[];
  initialSessionId?: string;
  onPublished?: (count: number) => void;
}

const chooseRecordingMimeType = () => {
  const candidates = [
    'video/mp4',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? '';
};

const fileExtensionForMime = (mimeType: string) => {
  if (mimeType === 'video/quicktime') return 'mov';
  if (mimeType === 'video/webm') return 'webm';
  return 'mp4';
};

const formatElapsed = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const videoContentType = (file: File) => {
  if (file.type) return file.type.split(';')[0];
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'mov') return 'video/quicktime';
  if (extension === 'webm') return 'video/webm';
  if (extension === 'mp4') return 'video/mp4';
  return '';
};

const readVideoDuration = (file: File) => new Promise<number | null>((resolve) => {
  if (!URL.createObjectURL) {
    resolve(null);
    return;
  }
  const video = document.createElement('video');
  const objectUrl = URL.createObjectURL(file);
  let finished = false;
  const finish = (duration: number | null) => {
    if (finished) return;
    finished = true;
    window.clearTimeout(timeout);
    video.removeAttribute('src');
    URL.revokeObjectURL?.(objectUrl);
    resolve(duration);
  };
  const timeout = window.setTimeout(() => finish(null), 5_000);
  video.preload = 'metadata';
  video.onloadedmetadata = () => finish(
    Number.isFinite(video.duration) ? video.duration : null,
  );
  video.onerror = () => finish(null);
  video.src = objectUrl;
});

export const PunchWalkthroughDialog: React.FC<PunchWalkthroughDialogProps> = ({
  open,
  onOpenChange,
  projectId,
  trades,
  itemTypes,
  locations,
  users = [],
  tradePartners = [],
  costCodes = [],
  initialSessionId,
  onPublished,
}) => {
  const [phase, setPhase] = useState<
    'intro' | 'recording' | 'uploading' | 'processing' | 'review' | 'published' | 'error'
  >(initialSessionId ? 'processing' : 'intro');
  const [sessionId, setSessionId] = useState<string | undefined>(initialSessionId);
  const [reviewDrafts, setReviewDrafts] = useState<PunchWalkthroughDraftUpdate[]>([]);
  const [activeDraftId, setActiveDraftId] = useState<string>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [photosUploading, setPhotosUploading] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const photoUploadInFlightRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const reviewSessionRef = useRef<string>();
  const reviewRevisionRef = useRef(0);
  const decisionInFlightRef = useRef(false);
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const recordingStartedRef = useRef(0);
  const recordingBytesRef = useRef(0);
  const recordingFailureRef = useRef('');
  const mountedRef = useRef(true);
  const pendingUploadRef = useRef<{ file: File; upload?: PunchWalkthroughUploadResponse }>();

  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder>();
  const mediaStreamRef = useRef<MediaStream>();
  const chunksRef = useRef<Blob[]>([]);

  const sessionQuery = usePunchWalkthrough(projectId, sessionId);
  const { upload, review, cancel, save, retry, uploadPhoto } = usePunchWalkthroughMutations(projectId);
  const session = sessionQuery.data;
  const activeSource = session?.drafts.find((draft) => draft.id === activeDraftId);
  const activeDraft = reviewDrafts.find((draft) => draft.id === activeDraftId);
  const currentIndex = session?.drafts.findIndex((draft) => draft.id === activeDraftId) ?? -1;
  const remainingCount = session?.drafts.filter(pendingReview).length ?? 0;
  const publishedCount = session?.drafts.filter((draft) => draft.review_status === 'PUBLISHED' || draft.published_item).length ?? 0;
  const discardedCount = session?.drafts.filter((draft) => draft.review_status === 'DISCARDED').length ?? 0;

  useEffect(() => {
    if (phase === 'review' && activeDraftId) reviewHeadingRef.current?.focus();
  }, [activeDraftId, phase]);

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = undefined;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; stopMediaTracks(); chunksRef.current = []; };
  }, []);

  useEffect(() => {
    if (phase !== 'recording') return undefined;
    if (videoRef.current) videoRef.current.srcObject = mediaStreamRef.current ?? null;
    const timer = window.setInterval(() => {
      const elapsed = Math.floor((performance.now() - recordingStartedRef.current) / 1_000);
      setElapsedSeconds(Math.min(MAX_RECORDING_SECONDS, elapsed));
      if (elapsed >= MAX_RECORDING_SECONDS - 1 && mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (!session || upload.isPending) return;
    if (session.status === 'UPLOADED' || session.status === 'PROCESSING') {
      setPhase('processing');
    } else if (session.status === 'READY') {
      if (reviewSessionRef.current !== session.id) {
        reviewSessionRef.current = session.id;
        reviewRevisionRef.current = session.revision ?? 0;
        setReviewDrafts(session.drafts.map(walkthroughDraftForReview));
        setActiveDraftId(session.drafts.find(pendingReview)?.id);
      }
      setPhase('review');
    } else if (session.status === 'FAILED') {
      setErrorMessage(
        session.processing_error_message || 'The walkthrough could not be processed.',
      );
      setPhase('error');
    } else if (session.status === 'PUBLISHED') {
      setPhase('published');
    } else if (session.status === 'CREATED' && !pendingUploadRef.current) {
      setErrorMessage('This upload was not completed. Retry the upload, or check whether storage received it.');
      setPhase('error');
    } else if (session.status === 'CANCELLED') {
      if (reviewSessionRef.current === session.id) {
        setPhase('published');
      } else {
        setErrorMessage('This walkthrough was discarded or its upload expired.');
        setPhase('error');
      }
    }
  }, [session, upload.isPending]);

  const uploadFile = async (file: File) => {
    if (!projectId) return;
    if (!['video/mp4', 'video/quicktime', 'video/webm'].includes(videoContentType(file))) {
      setErrorMessage('Record or choose an MP4, MOV, or WebM video.');
      setPhase('error');
      return;
    }
    if (file.size === 0 || file.size > MAX_VIDEO_BYTES) {
      setErrorMessage('The walkthrough must be smaller than 500 MB.');
      setPhase('error');
      return;
    }
    setPhase('uploading');
    const duration = await readVideoDuration(file);
    if (duration !== null && duration > MAX_RECORDING_SECONDS) {
      setErrorMessage('The walkthrough must be five minutes or shorter.');
      setPhase('error');
      return;
    }
    setPhase('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    const pending = pendingUploadRef.current?.file === file ? pendingUploadRef.current : { file };
    pendingUploadRef.current = pending;
    try {
      const created = await upload.mutateAsync({
        file,
        onProgress: setUploadProgress,
        resume: pending.upload,
        onCreated: (createdUpload) => {
          pending.upload = createdUpload;
          setSessionId(createdUpload.session.id);
        },
      });
      pendingUploadRef.current = undefined;
      setSessionId(created.id);
      setPhase('processing');
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setPhase('error');
    }
  };

  const startRecording = async () => {
    if (!projectId) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      fileInputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 }, frameRate: { ideal: 24, max: 30 } },
        audio: true,
      });
      const selectedMime = chooseRecordingMimeType();
      const recorder = selectedMime
        ? new MediaRecorder(stream, { mimeType: selectedMime, videoBitsPerSecond: 2_000_000, audioBitsPerSecond: 64_000 })
        : new MediaRecorder(stream, { videoBitsPerSecond: 2_000_000, audioBitsPerSecond: 64_000 });
      const baseMime = (recorder.mimeType || selectedMime || 'video/mp4').split(';')[0];
      chunksRef.current = [];
      recordingBytesRef.current = 0;
      recordingFailureRef.current = '';
      recordingStartedRef.current = performance.now();
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      if (videoRef.current) videoRef.current.srcObject = stream;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
        recordingBytesRef.current += event.data.size;
        if ((recordingBytesRef.current >= MAX_RECORDING_BYTES || performance.now() - recordingStartedRef.current >= 299_000)
            && recorder.state === 'recording') recorder.stop();
      });
      recorder.addEventListener('stop', () => {
        stopMediaTracks();
        if (!mountedRef.current) return;
        if (recordingFailureRef.current) {
          chunksRef.current = [];
          setErrorMessage(recordingFailureRef.current);
          setPhase('error');
          return;
        }
        const file = new File(
          chunksRef.current,
          `punch-walkthrough-${Date.now()}.${fileExtensionForMime(baseMime)}`,
          { type: baseMime },
        );
        chunksRef.current = [];
        void uploadFile(file);
      });
      recorder.addEventListener('error', () => {
        recordingFailureRef.current = 'The camera stopped recording. Please select an existing video or try again.';
        stopMediaTracks();
        setErrorMessage(recordingFailureRef.current);
        setPhase('error');
      });
      recorder.start(1_000);
      setElapsedSeconds(0);
      setErrorMessage('');
      setPhase('recording');
    } catch (error) {
      stopMediaTracks();
      setErrorMessage(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'Camera and microphone access are required to record a walkthrough.'
          : getErrorMessage(error),
      );
      setPhase('error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const updateDraft = <K extends keyof PunchWalkthroughDraftUpdate>(
    id: string,
    key: K,
    value: PunchWalkthroughDraftUpdate[K],
  ) => {
    if (review.isPending || save?.isPending || photosUploading || decisionInFlightRef.current || photoUploadInFlightRef.current) return;
    setDirty(true);
    setReviewDrafts((current) => current.map((draft) => (
      draft.id === id ? { ...draft, [key]: value } : draft
    )));
  };

  const addPhotos = async (files: File[]) => {
    if (!sessionId || !activeDraft || !files.length || photosUploading || photoUploadInFlightRef.current
        || decisionInFlightRef.current || review.isPending || save?.isPending) return;
    photoUploadInFlightRef.current = true;
    setPhotosUploading(true);
    setErrorMessage('');
    try {
      // Save current fields and removals first so each upload returns the complete saved selection.
      const saved = await save.mutateAsync({ sessionId, drafts: [activeDraft], revision: reviewRevisionRef.current });
      reviewRevisionRef.current = saved.revision ?? 0;
      setDirty(false);
      for (const file of files) {
        const result = await uploadPhoto.mutateAsync({ sessionId, draftId: activeDraft.id,
          revision: reviewRevisionRef.current, file });
        reviewRevisionRef.current = result.revision ?? 0;
        setReviewDrafts(result.drafts.map(walkthroughDraftForReview));
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      photoUploadInFlightRef.current = false;
      setPhotosUploading(false);
    }
  };

  const reviewCurrentDraft = async (decision: 'publish' | 'discard') => {
    if (!projectId || !sessionId || !activeDraft || review.isPending || save?.isPending || photosUploading || decisionInFlightRef.current || photoUploadInFlightRef.current) return;
    decisionInFlightRef.current = true;
    try {
      const common = { draft_id: activeDraft.id, revision: reviewRevisionRef.current };
      const result = await review.mutateAsync({ sessionId, input: decision === 'publish'
        ? { ...common, decision, draft: { ...activeDraft, included: true } }
        : { ...common, decision } });
      reviewRevisionRef.current = result.session.revision ?? 0;
      setReviewDrafts(result.session.drafts.map(walkthroughDraftForReview));
      setActiveDraftId(result.session.drafts.find(pendingReview)?.id);
      setDirty(false);
      setErrorMessage('');
      setPhase(['PUBLISHED', 'CANCELLED'].includes(result.session.status) ? 'published' : 'review');
      if (result.item_ids.length) onPublished?.(result.item_ids.length);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      decisionInFlightRef.current = false;
    }
  };

  const reset = () => {
    stopMediaTracks();
    // Starting another recording does not silently destroy an existing review.
    reviewSessionRef.current = undefined;
    pendingUploadRef.current = undefined;
    setDirty(false);
    setPhase('intro');
    setSessionId(undefined);
    setReviewDrafts([]);
    setActiveDraftId(undefined);
    setUploadProgress(0);
    setElapsedSeconds(0);
    setErrorMessage('');
  };

  const saveDrafts = async () => {
    if (!sessionId || !activeDraft || review.isPending || save?.isPending || photosUploading || decisionInFlightRef.current || photoUploadInFlightRef.current) return false;
    try {
      const saved = await save.mutateAsync({ sessionId, drafts: [activeDraft], revision: reviewRevisionRef.current });
      reviewRevisionRef.current = saved.revision ?? 0;
      setDirty(false);
      setErrorMessage('');
      return true;
    } catch (error) { setErrorMessage(getErrorMessage(error)); return false; }
  };
  const handleOpenChange = async (nextOpen: boolean) => {
    if (!nextOpen && (phase === 'recording' || phase === 'uploading' || review.isPending || save?.isPending || photosUploading || decisionInFlightRef.current || photoUploadInFlightRef.current)) return;
    if (!nextOpen && dirty && !(await saveDrafts())) return;
    onOpenChange(nextOpen);
  };
  const retryProcessing = async () => {
    if (!sessionId) return;
    try {
      await retry.mutateAsync({ sessionId, finalize: session?.status === 'CREATED' });
      setErrorMessage(''); setPhase('processing');
    } catch (error) { setErrorMessage(getErrorMessage(error)); setPhase('error'); }
  };
  const discard = async () => {
    if (!sessionId) return;
    try { await cancel.mutateAsync(sessionId); setDirty(false); onOpenChange(false); }
    catch (error) { setErrorMessage(getErrorMessage(error)); }
  };
  const reloadSavedDrafts = async () => {
    if (review.isPending || save?.isPending || photosUploading || decisionInFlightRef.current || photoUploadInFlightRef.current) return;
    const result = await sessionQuery.refetch();
    if (!result.data || result.isError) return;
    const saved = result.data;
    reviewRevisionRef.current = saved.revision ?? 0;
    setReviewDrafts(saved.drafts.map(walkthroughDraftForReview));
    setActiveDraftId(saved.drafts.find(pendingReview)?.id);
    setDirty(false);
    setErrorMessage('');
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => void handleOpenChange(nextOpen)}>
      <DialogContent className="max-w-3xl p-0" closeButtonClassName="right-4 top-4">
        <div className="border-b border-border px-5 pb-4 pt-5 sm:px-7">
          <DialogHeader>
            <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
              <Video className="size-4" strokeWidth={1.9} />
              AI punch walk
            </div>
            <DialogTitle ref={reviewHeadingRef} tabIndex={-1} className="focus:outline-none">
              {phase === 'review' && activeSource
                ? `Review item ${currentIndex + 1} of ${session?.drafts.length}`
                : phase === 'published' ? 'Walkthrough reviewed' : 'Record issues as you walk'}
            </DialogTitle>
            <DialogDescription>
              {phase === 'review' ? 'Publish or discard this item to continue. You can close and resume later.'
                : phase === 'published' ? 'Your review decisions have been saved.'
                  : 'Point the camera at each issue and describe what needs to be corrected.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5 px-5 pb-6 sm:px-7">
          {phase === 'intro' && (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  [Camera, 'Show the issue', 'Hold on each issue while you describe it.'],
                  [Mic, 'Say where it is', 'Name the room, level, or nearby landmark.'],
                  [ShieldCheck, 'Review first', 'AI drafts items; you decide what gets published.'],
                ].map(([Icon, title, copy]) => (
                  <div key={String(title)} className="rounded-[13px] border border-border bg-muted p-4">
                    <Icon className="mb-3 size-5 text-primary" strokeWidth={1.9} />
                    <div className="text-sm font-semibold">{String(title)}</div>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">{String(copy)}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                You can dictate any punch-item field: people and roles, due date, priority, status, trade and
                responsible company, type, location, cost code, cost/schedule impact, distribution list, and privacy.
                Name existing users and records clearly; review uncertain matches before publishing.
              </p>
              <div className="rounded-[13px] border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-muted-foreground">
                The raw video is deleted after successful processing. Failed uploads are retained for a 24-hour retry window, then cleaned up. Each suggested item starts with two
                issue photos. You can add or remove photos during review.
              </div>
              <DialogFooter>
                <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!projectId}
                  >
                    <Upload className="mr-2 size-4" />
                    Upload existing video
                  </Button>
                  <Button onClick={() => void startRecording()} disabled={!projectId}>
                    <Video className="mr-2 size-4" />
                    Record new walkthrough
                  </Button>
                </div>
              </DialogFooter>
              <p className="text-center text-xs text-muted-foreground">
                MP4, MOV, or WebM · five minutes maximum · 500 MB maximum
              </p>
            </div>
          )}

          {phase === 'recording' && (
            <div className="space-y-4">
              <div className="relative aspect-video overflow-hidden rounded-[1.375rem] border border-border bg-black">
                <video ref={videoRef} autoPlay muted playsInline className="size-full object-cover" />
                <Badge variant="destructive" className="absolute left-4 top-4 gap-2 bg-destructive text-destructive-foreground">
                  <span className="size-2 animate-pulse rounded-full bg-current" />
                  REC {formatElapsed(elapsedSeconds)}
                </Badge>
              </div>
              <p className="text-center text-sm text-muted-foreground">
                Pause on the issue and say “this issue” or “right here” before describing it.
              </p>
              <div className="flex justify-center">
                <Button variant="destructive" size="lg" onClick={stopRecording}>
                  <Square className="mr-2 size-4 fill-current" />
                  Finish recording
                </Button>
              </div>
            </div>
          )}

          {phase === 'uploading' && (
            <StatusPanel
              icon={Loader2}
              title="Uploading securely"
              copy="Keep this screen open while the recording uploads."
              spinning
            >
              <Progress value={uploadProgress} />
              <div className="mt-2 text-right text-xs font-semibold text-muted-foreground">
                {Math.round(uploadProgress)}%
              </div>
            </StatusPanel>
          )}

          {phase === 'processing' && (
            <StatusPanel
              icon={Loader2}
              title={{ queued: 'Waiting for a processing worker', downloading: 'Reading your video', validating: 'Checking the recording',
                transcribing: 'Transcribing your narration', analyzing: 'Finding punch items', extracting_frames: 'Selecting issue photos',
                retry_wait: 'Waiting to retry processing' }[session?.processing_stage ?? ''] ?? 'Loading walkthrough'}
              copy={session?.processing_error_message || 'You can close this screen. The walkthrough will stay in Saved AI reviews.'}
              spinning
            >
              {session?.attempt_count ? <p className="text-xs text-muted-foreground">Processing attempt {session.attempt_count}</p> : null}
              {sessionQuery.isError && <ErrorState error={sessionQuery.error} title="Could not refresh progress" onRetry={() => void sessionQuery.refetch()} />}
            </StatusPanel>
          )}

          {phase === 'review' && session && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">
                    {session.drafts.length} suggested item{session.drafts.length === 1 ? '' : 's'}
                  </div>
                  <p className="text-xs text-muted-foreground">{remainingCount} remaining · {publishedCount} published</p>
                </div>
              </div>

              {session.drafts.length === 0 ? (
                <div className="rounded-[1.375rem] border border-border bg-muted p-8 text-center">
                  <CheckCircle2 className="mx-auto mb-3 size-7 text-success" />
                  <div className="font-semibold">No clear punch items found</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Nothing will be created. You can record another walkthrough.
                  </p>
                  <Button variant="outline" className="mt-4" onClick={reset}>
                    <RotateCcw className="mr-2 size-4" />
                    Record again
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {session.drafts.map((source, index) => {
                    if (source.id !== activeDraftId) return null;
                    const draft = reviewDrafts.find((candidate) => candidate.id === source.id);
                    if (!draft) return null;
                    return (
                      <div
                        key={source.id}
                        role="group"
                        aria-label={`Punch item ${index + 1}`}
                        className="grid gap-4 rounded-[1.375rem] border border-border bg-card p-4 shadow-slate sm:grid-cols-[190px_1fr]"
                      >
                        <div className="min-w-0 space-y-3">
                          <AttachmentPhotoGallery
                            attachments={source.photos !== undefined
                              ? source.photos.filter((photo) => (draft.photo_ids ?? source.photo_ids ?? []).includes(photo.id))
                                .map((photo) => ({ ...photo, id: String(photo.id) }))
                              : [source.frame_url, source.secondary_frame_url].flatMap((url, photoIndex) => url
                                ? [{ id: String(photoIndex), file: `Captured issue ${index + 1}, photo ${photoIndex + 1}.jpg`, file_url: url }] : [])}
                            maxVisible={50}
                            gridClassName="grid grid-cols-2 gap-2"
                            onDelete={source.photos === undefined ? undefined : (id) => updateDraft(draft.id, 'photo_ids',
                              (draft.photo_ids ?? source.photo_ids ?? []).filter((photoId) => photoId !== Number(id)))}
                            isDeleting={review.isPending || save?.isPending || photosUploading}
                          />
                          {draft.photo_ids?.length === 0 && <p className="text-xs text-muted-foreground">No photos attached.</p>}
                          <Button type="button" size="sm" variant="outline" className="w-full"
                            disabled={review.isPending || save?.isPending || photosUploading || source.photos === undefined}
                            onClick={() => photoInputRef.current?.click()}>
                            {photosUploading ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Camera className="mr-2 size-4" />}
                            {photosUploading ? 'Uploading photos…' : 'Add photos'}
                          </Button>
                          <Input ref={photoInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden" aria-label="Add issue photos" disabled={photosUploading}
                            onChange={(event) => {
                              const files = Array.from(event.currentTarget.files ?? []);
                              event.currentTarget.value = '';
                              void addPhotos(files);
                            }} />
                          <p className="text-xs text-muted-foreground">Select a photo to enlarge it. Add or remove photos before publishing.</p>
                        </div>
                        <div className="min-w-0 space-y-3">
                          <WalkthroughItemFields draft={draft} spokenLocation={source.spoken_location ?? ''}
                            locations={locations} trades={trades} itemTypes={itemTypes}
                            users={users} tradePartners={tradePartners}
                            costCodes={costCodes} hotspots={session.hotspot_options ?? []}
                            disabled={review.isPending || save?.isPending || photosUploading}
                            onChange={(key, value) => updateDraft(draft.id, key, value)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {errorMessage && <div className="space-y-2">
                <p className="text-sm text-destructive">{errorMessage}</p>
                <Button variant="outline" size="sm" disabled={review.isPending || save?.isPending || photosUploading}
                  onClick={() => void reloadSavedDrafts()}>Reload saved version (discard local edits)</Button>
              </div>}
              {activeDraft && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => void saveDrafts()} disabled={review.isPending || save?.isPending || photosUploading || !dirty}>
                    {save?.isPending ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}
                  </Button>
                  <Button variant="outline" onClick={() => void reviewCurrentDraft('discard')} disabled={review.isPending || save?.isPending || photosUploading}>Discard</Button>
                  <Button
                    onClick={() => void reviewCurrentDraft('publish')}
                    disabled={review.isPending || save?.isPending || photosUploading}
                  >
                    {review.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                    {remainingCount > 1 ? 'Publish and see next' : 'Publish'}
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}

          {phase === 'published' && (
            <StatusPanel
              icon={CheckCircle2}
              title="Review complete"
              copy={publishedCount > 0
                ? `${publishedCount} punch item${publishedCount === 1 ? '' : 's'} published.${discardedCount ? ` ${discardedCount} draft${discardedCount === 1 ? '' : 's'} discarded.` : ''}`
                : 'All drafts were discarded. No punch items were created.'}
            >
              <DialogFooter className="mt-5">
                <Button variant="outline" onClick={reset}>Record another</Button>
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </DialogFooter>
            </StatusPanel>
          )}

          {phase === 'error' && (
            <StatusPanel
              icon={Video}
              title="Walkthrough stopped"
              copy={errorMessage || 'The walkthrough could not be completed.'}
            >
              <DialogFooter className="mt-5">
                <Button variant="outline" onClick={() => void handleOpenChange(false)}>Close</Button>
                {pendingUploadRef.current && <Button disabled={upload.isPending} onClick={() => void uploadFile(pendingUploadRef.current!.file)}>Resume upload</Button>}
                {!pendingUploadRef.current && sessionId && !session?.video_deleted && ['FAILED', 'CREATED'].includes(session?.status ?? '') &&
                  <Button disabled={retry?.isPending} onClick={() => void retryProcessing()}>{session?.status === 'CREATED' ? 'Check uploaded video' : 'Retry processing'}</Button>}
                {sessionId && session?.status !== 'PROCESSING' && session?.status !== 'PUBLISHED' && <Button variant="outline" onClick={() => void discard()}>Discard</Button>}
                <Button variant="outline" onClick={reset}>Choose another video</Button>
              </DialogFooter>
            </StatusPanel>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
            className="hidden"
            aria-label="Upload an existing walkthrough video"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void uploadFile(file);
              event.currentTarget.value = '';
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StatusPanel = ({
  icon: Icon,
  title,
  copy,
  spinning = false,
  children,
}: {
  icon: React.ElementType;
  title: string;
  copy: string;
  spinning?: boolean;
  children?: React.ReactNode;
}) => (
  <div className="py-6 text-center">
    <div className="mx-auto grid size-12 place-items-center rounded-[13px] bg-primary/12 text-primary">
      <Icon className={`size-6 ${spinning ? 'animate-spin' : ''}`} strokeWidth={1.9} />
    </div>
    <h3 className="mt-4 font-display text-lg font-extrabold tracking-[-0.02em]">{title}</h3>
    <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-muted-foreground">{copy}</p>
    <div className="mx-auto mt-5 max-w-md">{children}</div>
  </div>
);
